import React, { useState, useEffect } from 'react';
import { User, userApi } from '../../services/api';
import './UserForm.scss';

interface UserFormProps {
  user?: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    // User data
    email: '',
    password: '',
    user_type: 'employee' as 'employee' | 'employer' | 'admin',
    status: 'active' as 'active' | 'inactive' | 'suspended',
    email_verified: false,
    
    // Profile data
    first_name: '',
    last_name: '',
    phone: '',
    location: '',
    bio: '',
    company_name: '',
    company_size: '' as '' | '1-10' | '11-50' | '51-200' | '201-1000' | '1000+',
    linkedin_url: '',
    website_url: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        password: '', // Don't populate password for editing
        user_type: user.user_type as 'employee' | 'employer' | 'admin',
        status: user.status,
        email_verified: user.email_verified,
        first_name: user.profile?.first_name || '',
        last_name: user.profile?.last_name || '',
        phone: user.profile?.phone || '',
        location: user.profile?.location || '',
        bio: user.profile?.bio || '',
        company_name: user.profile?.company_name || '',
        company_size: (user.profile?.company_size as any) || '',
        linkedin_url: user.profile?.linkedin_url || '',
        website_url: user.profile?.website_url || '',
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare user data
      const userData: any = {
        email: formData.email,
        user_type: formData.user_type,
        status: formData.status,
        email_verified: formData.email_verified,
      };

      // Add password only if provided (for new users or password change)
      if (formData.password) {
        userData.password = formData.password;
      }

      // Prepare profile data
      const profileData: any = {};
      
      if (formData.first_name) profileData.first_name = formData.first_name;
      if (formData.last_name) profileData.last_name = formData.last_name;
      if (formData.phone) profileData.phone = formData.phone;
      if (formData.location) profileData.location = formData.location;
      if (formData.bio) profileData.bio = formData.bio;
      if (formData.company_name) profileData.company_name = formData.company_name;
      if (formData.company_size) profileData.company_size = formData.company_size;
      if (formData.linkedin_url) profileData.linkedin_url = formData.linkedin_url;
      if (formData.website_url) profileData.website_url = formData.website_url;

      const requestData = {
        user: userData,
        profile: Object.keys(profileData).length > 0 ? profileData : undefined,
      };

      let response;
      if (isEditing) {
        response = await userApi.updateUser(user!.id!, requestData);
      } else {
        response = await userApi.createUser(requestData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || 'Nepavyko išsaugoti naudotojo');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Klaida išsaugojant naudotoją');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-form-overlay">
      <div className="user-form">
        <div className="user-form__header">
          <h2>{isEditing ? 'Redaguoti Naudotoją' : 'Pridėti Naudotoją'}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="user-form__content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-section">
            <h3>Pagrindinė Informacija</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>El. paštas *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{isEditing ? 'Naujas slaptažodis (palikti tuščią, jei nekeisti)' : 'Slaptažodis *'}</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!isEditing}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Naudotojo tipas *</label>
                <select
                  name="user_type"
                  value={formData.user_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="employee">Darbuotojas</option>
                  <option value="employer">Darbdavys</option>
                  <option value="admin">Administratorius</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Būsena</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="active">Aktyvus</option>
                  <option value="inactive">Neaktyvus</option>
                  <option value="suspended">Sustabdytas</option>
                </select>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="email_verified"
                    checked={formData.email_verified}
                    onChange={handleInputChange}
                  />
                  El. paštas patvirtintas
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Profilio Informacija</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Vardas</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Pavardė</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Telefonas</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Vieta</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {formData.user_type === 'employer' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Kompanijos pavadinimas</label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Kompanijos dydis</label>
                    <select
                      name="company_size"
                      value={formData.company_size}
                      onChange={handleInputChange}
                    >
                      <option value="">Pasirinkti...</option>
                      <option value="1-10">1-10 darbuotojų</option>
                      <option value="11-50">11-50 darbuotojų</option>
                      <option value="51-200">51-200 darbuotojų</option>
                      <option value="201-1000">201-1000 darbuotojų</option>
                      <option value="1000+">1000+ darbuotojų</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>LinkedIn profilis</label>
                    <input
                      type="url"
                      name="linkedin_url"
                      value={formData.linkedin_url}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Kompanijos svetainė</label>
                    <input
                      type="url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label>Aprašymas</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows={4}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Atšaukti
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Išsaugoma...' : (isEditing ? 'Atnaujinti' : 'Sukurti')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;
